// Copyright ©️ 2026 eVolve MEP, LLC

import { type RefObject, useLayoutEffect, useRef } from 'react';

/** Matches the `height` transition on `.track` in `NavPanel.module.css`. In ms. */
const HEIGHT_TRANSITION_MS = 280;

/**
 * Animates an element's height from its old height to its new one whenever `paneKey` changes, as the nav panel does
 * when it swaps between the step list and a step. The element needs a CSS `height` transition; between changes its
 * height is left to its content.
 */
export const useAnimatedPaneHeight = (elementRef: RefObject<HTMLElement | null>, paneKey: string) => {
  // The height at the end of the last commit, so a pane change can start from it.
  const lastHeight = useRef<number | null>(null);
  const isAnimating = useRef(false);

  useLayoutEffect(() => {
    const element = elementRef.current;
    const previousHeight = lastHeight.current;
    if (element === null || previousHeight === null) return undefined;
    element.style.height = '';
    const nextHeight = element.offsetHeight;
    if (Math.abs(nextHeight - previousHeight) <= 1) return undefined;

    isAnimating.current = true;
    element.style.height = `${String(previousHeight)}px`;
    // Reading the height applies the start value before the end value is set, so the change transitions.
    void element.offsetHeight;
    element.style.height = `${String(nextHeight)}px`;
    lastHeight.current = nextHeight;
    const timeout = setTimeout(() => {
      element.style.height = '';
      isAnimating.current = false;
      lastHeight.current = element.offsetHeight;
    }, HEIGHT_TRANSITION_MS);
    return () => {
      clearTimeout(timeout);
      isAnimating.current = false;
    };
  }, [elementRef, /* effect dep */ paneKey]);

  // Declared after the effect above, so it records each commit's height once any animation has started.
  useLayoutEffect(() => {
    if (isAnimating.current || elementRef.current === null) return;
    lastHeight.current = elementRef.current.offsetHeight;
  });
};
